/**
 * Rental Product Form Handler
 * Manages rental length selection, dates, availability, and shipping
 */

class RentalProductForm extends HTMLElement {
  constructor() {
    super();
    
    // Configuration
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.calendarConfig = null;
    this.subscriptionConfig = null;
    
    // Parse metafield configurations from data attributes
    try {
      if (this.dataset.calendarConfig) {
        this.calendarConfig = JSON.parse(this.dataset.calendarConfig);
      }
      
      if (this.dataset.subscriptionConfig) {
        this.subscriptionConfig = JSON.parse(this.dataset.subscriptionConfig);
      }
    } catch (e) {
      // Silent fail - form will use defaults
    }

    // State
    this.state = {
      rentalType: 'short-term',
      startDate: null,
      endDate: null,
      address: null,
      quantity: 1,
      shippingRates: null,
      availability: null,
      selectedLocation: null,
      selectedShippingRate: null,
      maxQuantity: null,
      isLoading: false
    };

    // Elements
    this.elements = {};
    
    // Debounce timers
    this.debounceTimers = {};
  }

  connectedCallback() {
    // Wait for DOM to be fully loaded before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      // DOM already loaded, but wait a tick to ensure all elements are rendered
      setTimeout(() => this.initialize(), 0);
    }
  }

  async initialize() {
    this.initializeElements();
    this.disableAddToCart();
    document.body?.classList.add('rental-product-page');
    this.initializeEventListeners();
    await this.fetchAndSetMinimumDate();
    this.initializeDatePickers();
    this.loadPricesFromMetafields();
    this.loadDatesFromSession();
    this.watchForAddressChanges();
    this.watchForQuantityChanges();
    this.checkInitialLoad();
  }

  async fetchAndSetMinimumDate() {
    // Disable date displays while fetching
    this.disableDateDisplays();
    
    try {
      const data = await GetClubsAPI.getRentalLeadTime();
      console.log('Lead time response:', data);
      
      if (data.firstAvailableDate) {
        // Parse the YYYY-MM-DD date
        this.minimumDate = new Date(data.firstAvailableDate + 'T00:00:00');
        console.log('Minimum rental date set to:', this.minimumDate);
      } else {
        throw new Error('No firstAvailableDate in response');
      }
    } catch (error) {
      console.error('Error fetching lead time:', error);
      // Fallback to tomorrow if API fails
      this.minimumDate = new Date();
      this.minimumDate.setDate(this.minimumDate.getDate() + 1);
      console.warn('Using fallback minimum date (tomorrow):', this.minimumDate);
    } finally {
      this.enableDateDisplays();
    }
  }

  disableDateDisplays() {
    if (this.elements.startDateDisplay) {
      this.elements.startDateDisplay.style.opacity = '0.5';
      this.elements.startDateDisplay.style.pointerEvents = 'none';
    }
    if (this.elements.endDateDisplay) {
      this.elements.endDateDisplay.style.opacity = '0.5';
      this.elements.endDateDisplay.style.pointerEvents = 'none';
    }
  }

  enableDateDisplays() {
    if (this.elements.startDateDisplay) {
      this.elements.startDateDisplay.style.opacity = '1';
      this.elements.startDateDisplay.style.pointerEvents = 'auto';
    }
    if (this.elements.endDateDisplay) {
      this.elements.endDateDisplay.style.opacity = '1';
      this.elements.endDateDisplay.style.pointerEvents = 'auto';
    }
  }

  checkInitialLoad() {
    // Check if we have address and dates pre-loaded
    if (this.state.address && this.state.startDate && this.state.endDate) {
      console.log('Pre-loaded address and dates detected, making initial requests');
      this.requestAvailabilityAndShipping();
    }
  }

  loadDatesFromSession() {
    let startDate = sessionStorage.getItem('refulfil:dates:start');
    const endDate = sessionStorage.getItem('refulfil:dates:end');
    
    if (startDate) {
      console.log('Loading start date from session:', startDate);
      const startDateObj = this.parseStoredDate(startDate);
      
      // Validate against minimum date
      if (this.minimumDate && startDateObj < this.minimumDate) {
        console.warn('Saved start date is before minimum date, updating to minimum');
        startDate = this.formatDate(this.minimumDate);
        sessionStorage.setItem('refulfil:dates:start', startDate);
        console.log('Updated start date in session to:', startDate);
      }
      
      this.state.startDate = startDate;
      this.elements.rentalStart.value = this.formatDateForAPI(startDate);
      
      // Update display with validated date
      const validStartDateObj = this.parseStoredDate(startDate);
      if (validStartDateObj && this.datePicker) {
        this.datePicker.setDate(validStartDateObj, false); // Don't trigger onChange
        this.elements.startDateDisplay.querySelector('.value').textContent = this.datePicker.formatDate(validStartDateObj, 'd M Y');
      }
    }
    
    // Only load end date for short-term (long-term is always calculated)
    if (endDate && this.state.rentalType === 'short-term') {
      console.log('Loading end date from session:', endDate);
      this.state.endDate = endDate;
      this.elements.rentalEnd.value = this.formatDateForAPI(endDate);
      
      // Update display
      const endDateObj = this.parseStoredDate(endDate);
      if (endDateObj && this.datePicker && startDate) {
        const startDateObj = this.parseStoredDate(startDate);
        this.datePicker.setDate([startDateObj, endDateObj], false); // Set range
        this.elements.endDateDisplay.querySelector('.value').textContent = this.datePicker.formatDate(endDateObj, 'd M Y');
      }
    }
  }

  parseStoredDate(dateString) {
    // Parse MM/DD/YYYY format
    const [month, day, year] = dateString.split('/');
    return new Date(year, month - 1, day);
  }

  saveDatesToSession() {
    if (this.state.startDate) {
      sessionStorage.setItem('refulfil:dates:start', this.state.startDate);
      console.log('Saved start date to session:', this.state.startDate);
    }
    
    // Only save end date for short-term (long-term is calculated)
    if (this.state.rentalType === 'short-term' && this.state.endDate) {
      sessionStorage.setItem('refulfil:dates:end', this.state.endDate);
      console.log('Saved end date to session:', this.state.endDate);
    }
  }

  initializeElements() {
    // Rental type buttons
    this.elements.shortTermBtn = this.querySelector('[data-rental-type="short-term"]');
    this.elements.longTermBtn = this.querySelector('[data-rental-type="long-term"]');
    
    // Date display divs (clickable elements that show flatpickr)
    this.elements.startDateDisplay = this.querySelector('[data-start-date-display]');
    this.elements.endDateDisplay = this.querySelector('[data-end-date-display]');
    this.elements.startLabel = this.querySelector('[data-start-label]');
    this.elements.endLabel = this.querySelector('[data-end-label]');
    
    // Hidden inputs (within this component)
    this.elements.rentalStart = this.querySelector('[data-rental-start]');
    this.elements.rentalEnd = this.querySelector('[data-rental-end]');
    this.elements.rentalType = this.querySelector('[data-rental-type]');
    
    // Hidden inputs in the form (outside this component, in buy-buttons).
    // `_cycle` / `_validations` are populated dynamically from the Supercycle
    // intent response, so they're located via the form rather than cached here.
    this.elements.rentalPrice = document.querySelector('[data-rental-price]');
    this.elements.sellingPlan = document.querySelector('[data-selling-plan]');
    this.elements.firstDay = document.querySelector('[data-first-day]');
    this.elements.lastDay = document.querySelector('[data-last-day]');
    this.elements.leaseStart = document.querySelector('[data-lease-start]');
    this.elements.leaseEnd = document.querySelector('[data-lease-end]');
    
    
    // Price elements (from main product section)
    this.elements.rentalCost = document.querySelector('[data-rental-cost]');
    this.elements.shippingCost = document.querySelector('[data-shipping-cost]');
    this.elements.returnCost = document.querySelector('[data-return-cost]');
    this.elements.total = document.querySelector('[data-total]');
    
    console.log('Price elements found:', {
      rentalCost: !!this.elements.rentalCost,
      shippingCost: !!this.elements.shippingCost,
      returnCost: !!this.elements.returnCost,
      total: !!this.elements.total
    });
    
    // Quantity selector - find it dynamically
    this.elements.quantityInput = document.querySelector('.quantity__input[name="quantity"]') ||
                                   document.querySelector('input[name="quantity"]');
    this.elements.quantityButtons = document.querySelectorAll('.quantity__button');
    this.elements.quantityPlusBtn = document.querySelector('.quantity__button[name="plus"]');
    this.elements.quantityMinusBtn = document.querySelector('.quantity__button[name="minus"]');
    
    console.log('Quantity input found:', !!this.elements.quantityInput);
    if (this.elements.quantityInput) {
      console.log('Quantity input ID:', this.elements.quantityInput.id);
      console.log('Initial quantity:', this.elements.quantityInput.value);
    }
    
    // Add to cart button - find it dynamically
    this.elements.addToCartBtn = document.querySelector('button[name="add"]') ||
                                  document.querySelector('.product-form__submit');
    
    console.log('Add to cart button found:', !!this.elements.addToCartBtn);
    if (this.elements.addToCartBtn) {
      console.log('Add to cart button ID:', this.elements.addToCartBtn.id);
    }
  }

  initializeEventListeners() {
    // Rental type selection
    if (this.elements.shortTermBtn) {
      this.elements.shortTermBtn.addEventListener('click', () => this.selectRentalType('short-term'));
    }
    if (this.elements.longTermBtn) {
      this.elements.longTermBtn.addEventListener('click', () => this.selectRentalType('long-term'));
    }
  }

  initializeDatePickers() {
    if (!window.flatpickr) {
      console.error('Flatpickr not loaded');
      return;
    }

    // Use the minimum date fetched from the API
    const minDate = this.minimumDate || new Date();
    console.log('Initializing date picker with minDate:', minDate);

    // Initialize flatpickr on the start date display div
    this.datePicker = flatpickr(this.elements.startDateDisplay, {
      dateFormat: 'Y-m-d',
      minDate: minDate,
      locale: {
        firstDayOfWeek: 1
      },
      clickOpens: true,
      allowInput: false,
      disableMobile: true,
      mode: this.state.rentalType === 'short-term' ? 'range' : 'single',
      onChange: (selectedDates) => this.handleDateChange(selectedDates),
      onOpen: () => {
        // Add 'open' class for animation
        setTimeout(() => {
          const calendar = document.querySelector('.flatpickr-calendar');
          if (calendar) {
            calendar.classList.add('open');
          }
        }, 10);
      },
      onClose: () => {
        // Remove 'open' class for animation
        const calendar = document.querySelector('.flatpickr-calendar');
        if (calendar) {
          calendar.classList.remove('open');
        }
      }
    });

    // Make end date display clickable (opens the same picker)
    if (this.elements.endDateDisplay) {
      this.elements.endDateDisplay.addEventListener('click', () => {
        // Only allow clicking if not disabled
        if (!this.elements.endDateDisplay.classList.contains('disabled')) {
          this.elements.startDateDisplay.click();
        }
      });
    }

    // Close calendar on backdrop click (mobile)
    document.addEventListener('mousedown', (e) => {
      const calendar = document.querySelector('.flatpickr-calendar');
      if (calendar && calendar.classList.contains('open')) {
        const calendarRect = calendar.getBoundingClientRect();
        // Check if click is outside/above the calendar (on backdrop)
        if (e.clientY < calendarRect.top || 
            e.clientX < calendarRect.left || 
            e.clientX > calendarRect.right) {
          e.preventDefault();
          this.datePicker.close();
        }
      }
    });
  }


  selectRentalType(type) {
    const previousType = this.state.rentalType;
    this.state.rentalType = type;
    this.elements.rentalType.value = type;
    
    console.log('Rental type changed from', previousType, 'to:', type);
    
    // Update button states
    this.elements.shortTermBtn?.classList.toggle('active', type === 'short-term');
    this.elements.longTermBtn?.classList.toggle('active', type === 'long-term');
    
    // Update date labels and behavior
    if (type === 'short-term') {
      this.updateDateLabelsForShortTerm();
      this.configureDatePickersForShortTerm();
    } else {
      this.updateDateLabelsForLongTerm();
      this.configureDatePickersForLongTerm();
    }
    
    // Preserve start date and handle end date
    const hasStartDate = this.state.startDate;
    
    if (hasStartDate) {
      if (type === 'short-term') {
        // Switching to short-term: restore end date from session if available
        const savedEndDate = sessionStorage.getItem('refulfil:dates:end');
        if (savedEndDate) {
          console.log('Restoring end date from session:', savedEndDate);
          this.state.endDate = savedEndDate;
          
          // Clear and set as range
          const startDateObj = this.parseStoredDate(this.state.startDate);
          const endDateObj = this.parseStoredDate(savedEndDate);
          if (this.datePicker && startDateObj && endDateObj) {
            this.datePicker.clear();
            this.datePicker.setDate([startDateObj, endDateObj], false); // Set as range
            this.elements.startDateDisplay.querySelector('.value').textContent = this.datePicker.formatDate(startDateObj, 'd M Y');
            this.elements.endDateDisplay.querySelector('.value').textContent = this.datePicker.formatDate(endDateObj, 'd M Y');
          }
        } else {
          // No saved end date, clear it and only show start
          this.state.endDate = null;
          const startDateObj = this.parseStoredDate(this.state.startDate);
          if (this.datePicker && startDateObj) {
            this.datePicker.clear();
            this.datePicker.setDate(startDateObj, false); // Just start date
            this.elements.startDateDisplay.querySelector('.value').textContent = this.datePicker.formatDate(startDateObj, 'd M Y');
          }
          this.elements.endDateDisplay.querySelector('.value').textContent = 'Select dates';
        }
      } else {
        // Switching to long-term: calculate end date (3 months from start)
        console.log('Calculating end date (3 months from start)');
        const startDateObj = this.parseStoredDate(this.state.startDate);
        const endDateObj = new Date(startDateObj);
        endDateObj.setMonth(endDateObj.getMonth() + 3);
        
        this.state.endDate = this.formatDate(endDateObj);
        
        // Clear flatpickr and set only the start date (single mode)
        if (this.datePicker) {
          this.datePicker.clear();
          this.datePicker.setDate(startDateObj, false); // Don't trigger onChange
          this.elements.endDateDisplay.querySelector('.value').textContent = this.datePicker.formatDate(endDateObj, 'd M Y');
        }
      }
      
      // Update hidden inputs
      if (this.state.endDate) {
        this.elements.rentalEnd.value = this.formatDateForAPI(this.state.endDate);
      }
      
      // Only need to re-request availability (not shipping, since start date hasn't changed)
      if (this.state.address && this.state.endDate) {
        console.log('Re-requesting availability only (rental type changed, dates preserved)');
        this.requestAvailabilityOnly();
      }
    } else {
      // No dates set yet, just update the display
      if (this.state.selectedShippingRate) {
        this.updatePricingDisplay();
      }
    }
  }

  updateDateLabelsForShortTerm() {
    const shortTermStartLabel = this.elements.shortTermBtn?.dataset.shortTermLabel || 'First day of play';
    const endLabel = 'Last day of play';
    
    if (this.elements.startLabel) {
      this.elements.startLabel.textContent = 'First day of play';
    }
    if (this.elements.endLabel) {
      this.elements.endLabel.textContent = 'Last day of play';
    }
  }

  updateDateLabelsForLongTerm() {
    if (this.elements.startLabel) {
      this.elements.startLabel.textContent = 'Lease start';
    }
    if (this.elements.endLabel) {
      this.elements.endLabel.textContent = 'Lease end';
    }
  }

  configureDatePickersForShortTerm() {
    // Enable range mode for short term rentals
    if (this.datePicker) {
      this.datePicker.set('mode', 'range');
    }
    
    // Update placeholder text for short term
    if (this.elements.startDateDisplay) {
      const valueEl = this.elements.startDateDisplay.querySelector('.value');
      if (!this.state.startDate && valueEl) {
        valueEl.textContent = 'Select dates';
      }
    }
    
    // Enable end date display (not disabled)
    if (this.elements.endDateDisplay) {
      this.elements.endDateDisplay.classList.remove('disabled');
      const valueEl = this.elements.endDateDisplay.querySelector('.value');
      if (!this.state.endDate && valueEl) {
        valueEl.textContent = 'Select dates';
      }
    }
  }

  configureDatePickersForLongTerm() {
    // Enable single date selection for long term
    if (this.datePicker) {
      this.datePicker.set('mode', 'single');
    }
    
    // Update placeholder text for long term
    if (this.elements.startDateDisplay) {
      const valueEl = this.elements.startDateDisplay.querySelector('.value');
      if (!this.state.startDate && valueEl) {
        valueEl.textContent = 'Select a date';
      }
    }
    
    // Disable end date display (it's calculated)
    if (this.elements.endDateDisplay) {
      this.elements.endDateDisplay.classList.add('disabled');
      this.elements.endDateDisplay.querySelector('.value').textContent = '\u00A0'; // Non-breaking space
    }
  }

  handleDateChange(selectedDates) {
    const fp = this.datePicker;
    const previousStartDate = this.state.startDate;
    
    if (this.state.rentalType === 'short-term' && selectedDates.length === 2) {
      // Short term: range mode with 2 dates selected
      const [start, end] = selectedDates;
      
      // Format dates for storage (YYYY-MM-DD for API)
      const startFormatted = fp.formatDate(start, 'Y-m-d');
      const endFormatted = fp.formatDate(end, 'Y-m-d');
      
      // Format dates for display (d M Y like "6 Nov 2025")
      const startDisplay = fp.formatDate(start, 'd M Y');
      const endDisplay = fp.formatDate(end, 'd M Y');
      
      // Update state
      this.state.startDate = this.formatDate(start);
      this.state.endDate = this.formatDate(end);
      
      // Save to session storage
      this.saveDatesToSession();
      
      // Update hidden inputs (YYYY-MM-DD)
      this.elements.rentalStart.value = startFormatted;
      this.elements.rentalEnd.value = endFormatted;
      
      // Update display
      this.elements.startDateDisplay.querySelector('.value').textContent = startDisplay;
      this.elements.endDateDisplay.querySelector('.value').textContent = endDisplay;
      this.elements.endDateDisplay.classList.remove('disabled');
      
      // Update line item properties - clear long-term, set short-term
      if (this.elements.leaseStart) {
        this.elements.leaseStart.value = '';
        this.elements.leaseStart.removeAttribute('value');
      }
      if (this.elements.leaseEnd) {
        this.elements.leaseEnd.value = '';
        this.elements.leaseEnd.removeAttribute('value');
      }
      if (this.elements.firstDay) this.elements.firstDay.value = this.state.startDate;
      if (this.elements.lastDay) this.elements.lastDay.value = this.state.endDate;
      
      // Check if start date changed to determine if we need new shipping rates
      const startDateChanged = previousStartDate !== this.state.startDate;
      this.checkAndRequest(startDateChanged);
      
    } else if (this.state.rentalType === 'long-term' && selectedDates.length === 1) {
      // Long term: single date mode
      const [start] = selectedDates;
      
      // Calculate end date (3 months later)
      const endDate = new Date(start);
      endDate.setMonth(endDate.getMonth() + 3);
      
      // Format dates for storage (YYYY-MM-DD for API)
      const startFormatted = fp.formatDate(start, 'Y-m-d');
      const endFormatted = fp.formatDate(endDate, 'Y-m-d');
      
      // Format dates for display
      const startDisplay = fp.formatDate(start, 'd M Y');
      const endDisplay = fp.formatDate(endDate, 'd M Y');
      
      // Update state
      this.state.startDate = this.formatDate(start);
      this.state.endDate = this.formatDate(endDate);
      
      // Save to session storage (only start date for long-term)
      this.saveDatesToSession();
      
      // Update hidden inputs
      this.elements.rentalStart.value = startFormatted;
      this.elements.rentalEnd.value = endFormatted;
      
      // Update display
      this.elements.startDateDisplay.querySelector('.value').textContent = startDisplay;
      this.elements.endDateDisplay.querySelector('.value').textContent = endDisplay;
      
      // Update line item properties - clear short-term, set long-term
      if (this.elements.firstDay) {
        this.elements.firstDay.value = '';
        this.elements.firstDay.removeAttribute('value');
      }
      if (this.elements.lastDay) {
        this.elements.lastDay.value = '';
        this.elements.lastDay.removeAttribute('value');
      }
      if (this.elements.leaseStart) this.elements.leaseStart.value = this.state.startDate;
      if (this.elements.leaseEnd) this.elements.leaseEnd.value = this.state.endDate;
      
      // Check if start date changed to determine if we need new shipping rates
      const startDateChanged = previousStartDate !== this.state.startDate;
      this.checkAndRequest(startDateChanged);
    }
  }

  clearDates() {
    this.state.startDate = null;
    this.state.endDate = null;
    this.elements.rentalStart.value = '';
    this.elements.rentalEnd.value = '';
    
    // Clear date picker
    if (this.datePicker) {
      this.datePicker.clear();
    }
    
    // Reset display text based on rental type
    if (this.elements.startDateDisplay) {
      const startPlaceholder = this.state.rentalType === 'short-term' ? 'Select dates' : 'Select a date';
      this.elements.startDateDisplay.querySelector('.value').textContent = startPlaceholder;
    }
    if (this.elements.endDateDisplay) {
      const endPlaceholder = this.state.rentalType === 'short-term' ? 'Select dates' : '\u00A0'; // Non-breaking space for long-term
      this.elements.endDateDisplay.querySelector('.value').textContent = endPlaceholder;
    }
  }

  formatDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  formatDateForAPI(dateString) {
    // Convert MM/DD/YYYY to YYYY-MM-DD
    const [month, day, year] = dateString.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  watchForAddressChanges() {
    // Watch sessionStorage for address changes (from address picker)
    // The address picker stores data in separate keys, so we need to construct the object
    let lastAddressString = null;
    
    const checkAddress = () => {
      // Get all address components from sessionStorage
      const address1 = sessionStorage.getItem('refulfil:destination:address');
      const address2 = sessionStorage.getItem('refulfil:destination:address2');
      const city = sessionStorage.getItem('refulfil:destination:city');
      const state = sessionStorage.getItem('refulfil:destination:state');
      const zip = sessionStorage.getItem('refulfil:destination:postal');

      // Skip if no address data at all
      if (!address1 && !city && !zip) {
        return;
      }

      // Create a string representation for comparison
      const addressString = JSON.stringify({ address1, address2, city, state, zip });

      // Skip if same as last check
      if (addressString === lastAddressString) {
        return;
      }

      lastAddressString = addressString;

      // Construct address object
      const address = {
        address1: address1 || '',
        address2: address2 || '',
        city: city || '',
        state: state || '',
        province_code: state || '',
        zip: zip || ''
      };
      
      console.log('Address detected from sessionStorage:', address);
      
      // Validate that we have at least city and zip (minimum required)
      if (address.city && address.zip) {
        console.log('Valid address, updating state');
        this.state.address = address;
        this.checkAndRequest();
      } else {
        console.log('Incomplete address data, waiting for more info');
      }
    };

    // Check every 500ms
    setInterval(checkAddress, 500);
    checkAddress(); // Check immediately
  }

  watchForQuantityChanges() {
    if (this.elements.quantityInput) {
      // Set initial quantity from input
      this.state.quantity = parseInt(this.elements.quantityInput.value) || 1;
      console.log('Initial quantity set to:', this.state.quantity);
      
      // Listen to both input and change events
      const updateQuantity = () => {
        const newQuantity = parseInt(this.elements.quantityInput.value) || 1;
        console.log('Quantity input changed to:', newQuantity, 'current state:', this.state.quantity);
        if (newQuantity !== this.state.quantity) {
          this.state.quantity = newQuantity;
          this.handleQuantityChange();
        }
      };
      
      this.elements.quantityInput.addEventListener('input', updateQuantity);
      this.elements.quantityInput.addEventListener('change', updateQuantity);
      
      // Also watch for custom quantity update events (from quantity-input component)
      document.addEventListener('quantity:update', (e) => {
        if (e.detail && e.detail.quantity !== undefined) {
          const newQuantity = parseInt(e.detail.quantity) || 1;
          if (newQuantity !== this.state.quantity) {
            this.state.quantity = newQuantity;
            this.handleQuantityChange();
          }
        }
      });
      
      // MutationObserver as fallback to catch value changes
      const observer = new MutationObserver(() => {
        const newQuantity = parseInt(this.elements.quantityInput.value) || 1;
        if (newQuantity !== this.state.quantity) {
          console.log('Quantity changed via mutation observer:', newQuantity);
          this.state.quantity = newQuantity;
          this.handleQuantityChange();
        }
      });
      
      observer.observe(this.elements.quantityInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    } else {
      console.warn('Quantity input not found for watching changes');
    }
  }

  async handleQuantityChange() {
    console.log('Quantity changed to:', this.state.quantity);

    // If we have availability and rates data, update location and pricing
    if (this.state.availability && this.state.shippingRates) {
      // Check if we need to select a different location based on new quantity
      await this.selectBestLocation();
    } else if (this.state.selectedShippingRate) {
      // If we already have a selected rate but no need to reselect location,
      // just update the pricing display to reflect new quantity
      this.updatePricingDisplay();
    }
  }

  checkAndRequest(needsNewShipping = true) {
    // Debug logging
    console.log('checkAndRequest called', {
      hasAddress: !!this.state.address,
      address: this.state.address,
      hasStartDate: !!this.state.startDate,
      startDate: this.state.startDate,
      hasEndDate: !!this.state.endDate,
      endDate: this.state.endDate,
      needsNewShipping
    });
    
    // Only request if we have both address and dates
    if (this.state.address && this.state.startDate && this.state.endDate) {
      if (needsNewShipping) {
        console.log('All conditions met, requesting availability and shipping...');
        this.requestAvailabilityAndShipping();
      } else {
        console.log('Only requesting availability (shipping rates still valid)...');
        this.requestAvailabilityOnly();
      }
    } else {
      console.log('Missing required data - not making API requests');
    }
  }

  async requestAvailabilityAndShipping() {
    if (this.state.isLoading) return;
    
    this.state.isLoading = true;
    this.showLoadingState();
    this.disableAddToCart();

    try {
      console.log('Fetching availability and shipping rates...');
      
      // Make parallel requests
      const [availability, shippingRates] = await Promise.all([
        this.fetchAvailability(),
        this.fetchShippingRates()
      ]);

      console.log('Availability response:', availability);
      console.log('Shipping rates response:', shippingRates);

      this.state.availability = availability;
      this.state.shippingRates = shippingRates;

      // Calculate max quantity from availability
      this.calculateMaxQuantity();

      // Select best location, then create the intent (which re-enables Add to Cart)
      await this.selectBestLocation();

      this.hideLoadingState();
    } catch (error) {
      console.error('Error fetching availability and shipping:', error);
      console.error('Error stack:', error.stack);
      this.showError('Unable to check availability. Please try again.');
      this.disableAddToCart();
    } finally {
      this.state.isLoading = false;
    }
  }

  async requestAvailabilityOnly() {
    if (this.state.isLoading) return;
    
    this.state.isLoading = true;
    this.showLoadingState('Checking availability...');
    this.disableAddToCart();

    try {
      console.log('Fetching availability only (reusing existing shipping rates)...');
      
      const availability = await this.fetchAvailability();

      console.log('Availability response:', availability);

      this.state.availability = availability;
      // Keep existing shippingRates

      // Calculate max quantity from availability
      this.calculateMaxQuantity();

      // Select best location, then create the intent (which re-enables Add to Cart)
      await this.selectBestLocation();

      this.hideLoadingState();
    } catch (error) {
      console.error('Error fetching availability:', error);
      console.error('Error stack:', error.stack);
      this.showError('Unable to check availability. Please try again.');
      this.disableAddToCart();
    } finally {
      this.state.isLoading = false;
    }
  }

  async fetchAvailability() {
    const proxyPathPrefix = '/apps/supercycle';
    const variantId = this.variantId;
    
    const params = new URLSearchParams({
      rental_start: this.formatDateForAPI(this.state.startDate),
      rental_end: this.formatDateForAPI(this.state.endDate),
      delivery_method_type: 'shipping'
    });

    const response = await fetch(`${proxyPathPrefix}/variants/${variantId}/location_inventories?${params}`);
    
    if (!response.ok) {
      throw new Error(`Availability request failed: ${response.status}`);
    }

    return await response.json();
  }

  async fetchShippingRates(specificLocation = null) {
    // Get weight from variant (assuming it's available on the product)
    const variantWeight = this.getVariantWeight();

    const destAddress2 = this.state.address.address2;
    const payload = {
      destinationAddress: {
        address: this.state.address.address1,
        ...(destAddress2 ? { address2: destAddress2 } : {}),
        city: this.state.address.city,
        stateCode: this.state.address.province_code || this.state.address.state,
        postalCode: this.state.address.zip
      },
      fromRentalDate: this.formatDateForAPI(this.state.startDate),
      toRentalDate: this.state.rentalType === 'short-term' ? this.formatDateForAPI(this.state.endDate) : null,
      addReturnCost: this.state.rentalType === 'short-term',
      variant: `gid://shopify/ProductVariant/${this.variantId}`,
      packageDetails: {
        weight: {
          value: variantWeight.value,
          units: variantWeight.units
        }
      }
    };

    // If requesting for a specific location
    if (specificLocation) {
      payload.originLocation = specificLocation;
    }

    console.log('Shipping request payload:', payload);

    return GetClubsAPI.getRentalShippingRates(payload);
  }

  getVariantWeight() {
    // Get weight directly from data attributes (set via Liquid)
    const weight = parseFloat(this.dataset.variantWeight);
    const unit = this.dataset.variantWeightUnit;
    
    if (weight && unit) {
      const normalizedUnit = this.normalizeWeightUnit(unit);
      console.log('Variant weight from data attributes:', { value: weight, units: normalizedUnit });
      return {
        value: weight,
        units: normalizedUnit
      };
    }
    
    // Default weight if data attributes are missing
    console.warn('Variant weight not found in data attributes, using default');
    return {
      value: 30,
      units: 'LB'
    };
  }

  normalizeWeightUnit(unit) {
    // Normalize weight units to match API expectations
    if (!unit) return 'LB';
    
    const upperUnit = unit.toUpperCase();
    
    // Convert common variants to LB
    if (upperUnit === 'POUNDS' || upperUnit === 'LBS' || upperUnit === 'LB') {
      return 'LB';
    }
    
    // Convert kg variants
    if (upperUnit === 'KILOGRAMS' || upperUnit === 'KGS' || upperUnit === 'KG') {
      return 'KG';
    }
    
    // Return as-is if already in correct format
    return upperUnit;
  }

  calculateMaxQuantity() {
    if (!this.state.availability || !this.state.availability.inventory) {
      this.state.maxQuantity = 0;
      return;
    }

    // Find the maximum availableForDates across all locations
    const maxAvailable = this.state.availability.inventory.availableForDates || 0;
    this.state.maxQuantity = maxAvailable;
    
    console.log('Calculated max quantity:', this.state.maxQuantity);

    // Update quantity selector to disable plus button at max
    this.updateQuantitySelector();
    
    // If current quantity exceeds max, cap it
    if (this.state.quantity > this.state.maxQuantity && this.elements.quantityInput) {
      console.log('Current quantity exceeds max, capping at:', this.state.maxQuantity);
      this.state.quantity = this.state.maxQuantity;
      this.elements.quantityInput.value = this.state.maxQuantity;
    }
  }

  updateQuantitySelector() {
    if (!this.elements.quantityInput) {
      console.warn('Quantity input not found for updating');
      return;
    }
    
    const currentQty = parseInt(this.elements.quantityInput.value) || 1;
    
    console.log('Updating quantity selector - max:', this.state.maxQuantity, 'current:', currentQty);
    
    // Set max attribute on the input
    if (this.state.maxQuantity) {
      this.elements.quantityInput.setAttribute('max', this.state.maxQuantity);
      this.elements.quantityInput.setAttribute('data-max', this.state.maxQuantity);
      console.log('Set max quantity to:', this.state.maxQuantity);
    }
    
    // Disable/enable plus button based on max
    if (this.elements.quantityPlusBtn) {
      if (this.state.maxQuantity && currentQty >= this.state.maxQuantity) {
        this.elements.quantityPlusBtn.disabled = true;
        this.elements.quantityPlusBtn.setAttribute('aria-disabled', 'true');
        console.log('Disabled plus button (at max)');
      } else {
        this.elements.quantityPlusBtn.disabled = false;
        this.elements.quantityPlusBtn.removeAttribute('aria-disabled');
      }
    }
  }

  async selectBestLocation() {
    if (!this.state.availability || !this.state.shippingRates) {
      console.warn('Missing availability or shipping data');
      return;
    }

    const quantity = this.state.quantity;
    const locations = this.state.availability.inventory.locations || [];
    const warehouses = this.state.shippingRates.warehouses || [];

    console.log('Selecting best location:', { quantity, locations, warehouses });

    // Warehouses arrive ordered by proximity (nearest first). Pick the first
    // one whose inventory covers the requested quantity. The warehouse's
    // `rentalPrices` array already contains every still-feasible service
    // sorted cheapest first, so no further service-level selection is
    // needed at this stage.
    let selectedWarehouse = null;
    let selectedLocationData = null;

    for (const warehouse of warehouses) {
      const locationShopifyId = warehouse.location.id;
      console.log('Checking warehouse:', locationShopifyId);
      const locationInventory = locations.find((loc) => loc.shopifyId === locationShopifyId);
      console.log('Found inventory for location:', locationInventory);

      if (locationInventory && locationInventory.inventory.availableForDates >= quantity) {
        selectedWarehouse = warehouse;
        selectedLocationData = locationInventory;
        console.log('Selected warehouse:', locationShopifyId, 'with', locationInventory.inventory.availableForDates, 'available');
        break;
      }
    }

    if (!selectedWarehouse) {
      console.log('No warehouse has enough inventory — requesting alternative...');
      await this.requestShippingForAvailableLocation(locations, quantity);
      return;
    }

    // We found a good location
    this.state.selectedLocation = selectedLocationData;
    this.state.selectedShippingRate = selectedWarehouse;

    console.log('Successfully selected location and shipping rate');

    // Update pricing display
    this.updatePricingDisplay();

    // Create the Supercycle rental intent (sets _cycle / _validations / selling_plan)
    await this.buildRentalIntent();
  }

  async requestShippingForAvailableLocation(locations, quantity) {
    // Find a location with enough inventory
    const availableLocation = locations.find(loc => loc.inventory.availableForDates >= quantity);
    
    if (!availableLocation) {
      this.showError('Not enough inventory available for the selected quantity and dates.');
      this.disableAddToCart();
      return;
    }

    // Request shipping specifically for this location
    try {
      this.showLoadingState('Recalculating shipping...');
      
      const locationGid = `gid://shopify/Location/${availableLocation.shopifyId}`;
      const shippingRates = await this.fetchShippingRates(locationGid);
      
      // Update state with new shipping rates
      if (shippingRates.warehouses && shippingRates.warehouses.length > 0) {
        this.state.selectedShippingRate = shippingRates.warehouses[0];
        this.state.selectedLocation = availableLocation;

        this.updatePricingDisplay();
        this.hideLoadingState();
        // buildRentalIntent re-enables Add to Cart only once the intent resolves
        await this.buildRentalIntent();
      } else {
        throw new Error('No shipping rates available for this location');
      }
    } catch (error) {
      console.error('Error fetching shipping for specific location:', error);
      this.showError('Unable to calculate shipping. Please try again.');
      this.disableAddToCart();
    }
  }


  updatePricingDisplay() {
    if (!this.state.selectedShippingRate) {
      console.warn('No selected shipping rate to display');
      return;
    }

    const warehouse = this.state.selectedShippingRate;
    const cheapest = warehouse.rentalPrices?.[0];
    if (!cheapest) {
      console.warn('Selected warehouse has no rental prices');
      return;
    }
    const totalPerItem = parseFloat(cheapest.price);
    const returnPerItem = parseFloat(cheapest.returnAmount || '0');
    const outboundPerItem = totalPerItem - returnPerItem;
    const rentalPrice = this.getRentalPrice();
    const quantity = this.state.quantity;

    console.log('Updating pricing display:', { warehouse, totalPerItem, rentalPrice, quantity });

    const pdpRatesBlock = document.querySelector('.pdp-rates');
    if (pdpRatesBlock) {
      pdpRatesBlock.classList.add('show');
    }

    // Update shipping cost (outbound only, multiplied by quantity)
    if (this.elements.shippingCost) {
      const shippingTotal = outboundPerItem * quantity;
      this.elements.shippingCost.textContent = this.formatMoney(shippingTotal * 100);
      this.elements.shippingCost.dataset.shippingCost = outboundPerItem;
      console.log('Updated shipping cost:', shippingTotal);
    }

    // Update return cost if applicable
    if (this.elements.returnCost && this.state.rentalType === 'short-term') {
      const returnTotal = returnPerItem * quantity;
      if (returnPerItem > 0) {
        this.elements.returnCost.textContent = this.formatMoney(returnTotal * 100);
        this.elements.returnCost.dataset.returnCost = returnPerItem;
      } else {
        this.elements.returnCost.textContent = 'Free';
        this.elements.returnCost.dataset.returnCost = 0;
      }
      console.log('Updated return cost:', returnTotal);
    }

    // Update rental cost (multiplied by quantity)
    if (this.elements.rentalCost && rentalPrice) {
      const rentalTotal = (rentalPrice / 100) * quantity;
      
      // For long-term, add " / month" suffix
      if (this.state.rentalType === 'long-term') {
        this.elements.rentalCost.textContent = this.formatMoney(rentalPrice * quantity) + ' / month';
      } else {
        this.elements.rentalCost.textContent = this.formatMoney(rentalPrice * quantity);
      }
      
      this.elements.rentalCost.dataset.rentalCost = rentalPrice / 100; // Store per-item cost in dollars
      console.log('Updated rental cost:', rentalTotal);
    } else {
      console.warn('Could not update rental cost', { hasElement: !!this.elements.rentalCost, rentalPrice });
    }

    // Update total label for long-term
    const totalLabel = document.querySelector('.pdp-rates__total strong:first-child');
    if (totalLabel) {
      if (this.state.rentalType === 'long-term') {
        totalLabel.textContent = 'Total to pay today';
      } else {
        totalLabel.textContent = 'Total';
      }
    }

    // Update total (sum of all costs multiplied by quantity)
    if (this.elements.total) {
      const shippingTotal = parseFloat(this.elements.shippingCost?.dataset.shippingCost || 0) * quantity;
      const returnTotal = parseFloat(this.elements.returnCost?.dataset.returnCost || 0) * quantity;
      const rentalTotal = parseFloat(this.elements.rentalCost?.dataset.rentalCost || 0) * quantity;
      
      const total = shippingTotal + returnTotal + rentalTotal;
      this.elements.total.textContent = this.formatMoney(total * 100);
      this.elements.total.dataset.total = total;
      console.log('Updated total:', total);
    }
  }

  getRentalPrice() {
    console.log('Getting rental price for type:', this.state.rentalType);
    console.log('Calendar config:', this.calendarConfig);
    console.log('Subscription config:', this.subscriptionConfig);
    
    // Get rental price from metafields based on rental type
    if (this.state.rentalType === 'short-term') {
      // Calculate days between start and end to find correct price
      if (this.state.startDate && this.state.endDate && this.calendarConfig && this.calendarConfig.rental_periods) {
        const start = new Date(this.formatDateForAPI(this.state.startDate));
        const end = new Date(this.formatDateForAPI(this.state.endDate));
        // Add 1 to make it inclusive (same day = 1 day, not 0)
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        
        console.log('Calculated rental duration:', days, 'days');
        
        // Find matching rental period for this duration
        const matchingPeriod = this.calendarConfig.rental_periods.find(
          period => period.duration_count === days && period.duration_unit === 'day'
        );
        
        if (matchingPeriod) {
          const price = matchingPeriod.total_price || matchingPeriod.price_cents;
          console.log('Short-term rental price for', days, 'days (cents):', price);
          return price; // Already in cents
        } else {
          console.warn('No matching rental period found for', days, 'days');
          // Fallback to minimum price if no exact match
          const prices = this.calendarConfig.rental_periods.map(period => period.total_price || period.price_cents);
          const minPrice = Math.min(...prices);
          console.log('Using fallback minimum price (cents):', minPrice);
          return minPrice;
        }
      } else {
        console.warn('Missing dates or calendar configuration');
      }
    } else {
      // Get price from subscription_configuration for 3-month lease
      if (this.subscriptionConfig && this.subscriptionConfig.pricing_groups) {
        const threeMonthPlan = this.subscriptionConfig.pricing_groups.find(
          group => group.minimum_term === '3 months'
        );
        if (threeMonthPlan) {
          console.log('Long-term rental price (cents):', threeMonthPlan.initial_price_cents);
          return threeMonthPlan.initial_price_cents;
        } else {
          console.warn('No 3-month plan found in pricing groups');
        }
      } else {
        console.warn('No subscription configuration found');
      }
    }
    console.warn('Could not determine rental price, returning null');
    return null;
  }

  async buildRentalIntent() {
    if (!this.state.selectedLocation || !this.state.availability) {
      console.warn('Cannot build rental intent - missing location or availability');
      return;
    }

    // Set the signed `_rental_price` array (every still-feasible service for
    // the selected warehouse). The carrier-service callback picks the
    // currently-applicable entry by time on every cart recalculation; the
    // cleanup script prunes expired entries and removes the line if none
    // remain. No `_shipping_price` is set for rental — the array is the
    // single source of truth for the rental's shipping price. This is
    // getclubs-specific and independent of Supercycle's rental token.
    if (this.elements.rentalPrice && this.state.selectedShippingRate) {
      const prices = this.state.selectedShippingRate.rentalPrices || [];
      this.elements.rentalPrice.value = JSON.stringify(prices);
      console.log('Set _rental_price array length:', prices.length);
    } else {
      console.warn('Rental price input not found or no shipping rate selected');
    }

    // The delivery address is no longer written to the cart line. It is captured
    // once in `refulfil:destination:*` sessionStorage and persisted to the order
    // as `_dest_*` cart attributes at checkout (goToCheckoutWithPrefill).

    // Set date properties based on rental type
    // IMPORTANT: Clear the properties we DON'T want first, then set the ones we DO want
    if (this.state.rentalType === 'short-term') {
      // Clear long-term properties
      if (this.elements.leaseStart) {
        this.elements.leaseStart.value = '';
        this.elements.leaseStart.removeAttribute('value');
      }
      if (this.elements.leaseEnd) {
        this.elements.leaseEnd.value = '';
        this.elements.leaseEnd.removeAttribute('value');
      }

      // Set short-term properties
      if (this.elements.firstDay) {
        this.elements.firstDay.value = this.state.startDate;
        console.log('Set First day of play:', this.elements.firstDay.value);
      }
      if (this.elements.lastDay) {
        this.elements.lastDay.value = this.state.endDate;
        console.log('Set Last day of play:', this.elements.lastDay.value);
      }
    } else {
      // Clear short-term properties
      if (this.elements.firstDay) {
        this.elements.firstDay.value = '';
        this.elements.firstDay.removeAttribute('value');
      }
      if (this.elements.lastDay) {
        this.elements.lastDay.value = '';
        this.elements.lastDay.removeAttribute('value');
      }

      // Set long-term properties
      if (this.elements.leaseStart) {
        this.elements.leaseStart.value = this.state.startDate;
        console.log('Set Lease start:', this.elements.leaseStart.value);
      }
      if (this.elements.leaseEnd) {
        this.elements.leaseEnd.value = this.state.endDate;
        console.log('Set Lease end:', this.elements.leaseEnd.value);
      }
    }

    // Create the Supercycle intent. This replaces the legacy
    // `_rental_intent_token` line item property: the /intents endpoint returns
    // the `_cycle` and `_validations` line item properties (plus a
    // `selling_plan` id) that Supercycle now uses to turn the line into a Cycle.
    const reqId = (this._intentRequestId = (this._intentRequestId || 0) + 1);
    this.disableAddToCart();

    try {
      const intent = await this.fetchIntent();

      // Ignore stale responses — dates/quantity/address may have changed while
      // this request was in flight, superseding it with a newer intent.
      if (reqId !== this._intentRequestId) {
        console.log('Ignoring stale intent response');
        return;
      }

      this.applyIntentAttributes(intent.attributes);

      // Add-ons (deposits, card authorisations) must be added to the cart as
      // separate line items. The standard single-item product form submit can't
      // carry them, so surface a warning if any are returned. Handle here if the
      // store starts configuring add-ons on rental products.
      this.state.intentAddOns = intent.add_ons || [];
      if (this.state.intentAddOns.length > 0) {
        console.warn(
          'Supercycle intent returned add-ons that the rental product form does not yet add to the cart:',
          this.state.intentAddOns
        );
      }

      console.log('Applied intent attributes:', intent.attributes);
      this.enableAddToCart();
    } catch (error) {
      if (reqId !== this._intentRequestId) return;
      console.error('Error creating rental intent:', error);
      this.showError('Unable to confirm rental availability. Please try again.');
      this.disableAddToCart();
    }
  }

  // Resolve the selected rental option's Supercycle `global_id` from the product
  // metafield config. The /intents endpoint requires this to identify which
  // rental option the customer chose. The value is read verbatim from the
  // matched option (format: `gid://supercycle/CalendarOption/<id>`).
  getOptionGlobalId() {
    if (this.state.rentalType === 'short-term') {
      if (this.state.startDate && this.state.endDate && this.calendarConfig && this.calendarConfig.rental_periods) {
        const start = new Date(this.formatDateForAPI(this.state.startDate));
        const end = new Date(this.formatDateForAPI(this.state.endDate));
        // Add 1 to make it inclusive (same day = 1 day, not 0)
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        const match = this.calendarConfig.rental_periods.find(
          period => period.duration_count === days && period.duration_unit === 'day'
        );
        return match?.global_id || null;
      }
    } else {
      const group = this.subscriptionConfig?.pricing_groups?.find(
        g => g.minimum_term === '3 months'
      );
      return group?.global_id || null;
    }
    return null;
  }

  async fetchIntent() {
    const proxyPathPrefix = '/apps/supercycle';

    const globalId = this.getOptionGlobalId();
    if (!globalId) {
      throw new Error('Could not resolve rental option global_id for the selected dates');
    }

    // The /intents endpoint expects the Shopify variant ID (from data-variant-id),
    // NOT Supercycle's internal variant id (`availability.variant.id`, which the
    // legacy token used).
    const variantId = typeof this.variantId === 'string' ? parseInt(this.variantId, 10) : this.variantId;

    const params = {
      rental_start: this.formatDateForAPI(this.state.startDate),
      delivery_method_type: 'shipping'
    };
    const locationId = this.state.selectedLocation?.shopifyId;
    if (locationId) {
      params.location_id = locationId;
    }

    const body = {
      variant_id: variantId,
      option: {
        global_id: globalId,
        params
      }
    };

    console.log('Creating intent with payload:', body);

    const response = await fetch(`${proxyPathPrefix}/intents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Intent request failed: ${response.status}`);
    }

    return response.json();
  }

  // Attach the intent's `attributes` to the add-to-cart form. Each key is
  // already the correct form field name (e.g. `properties[_cycle]`,
  // `properties[_validations]`, `selling_plan`), so set/create inputs verbatim.
  applyIntentAttributes(attributes) {
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) {
      console.warn('Add-to-cart form not found - cannot apply intent attributes');
      return;
    }

    Object.entries(attributes || {}).forEach(([name, value]) => {
      let input = form.querySelector(`[name="${CSS.escape(name)}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value == null ? '' : value;
    });
  }

  loadPricesFromMetafields() {
    // Update price displays based on metafields
    const shortTermPrice = this.querySelector('[data-short-term-price]');
    const longTermPrice = this.querySelector('[data-long-term-price]');

    if (shortTermPrice && this.calendarConfig && this.calendarConfig.rental_periods) {
      const prices = this.calendarConfig.rental_periods.map(period => period.total_price);
      const minPrice = Math.min(...prices);
      shortTermPrice.textContent = `From ${this.formatMoney(minPrice)}`;
    }

    if (longTermPrice && this.subscriptionConfig && this.subscriptionConfig.pricing_groups) {
      const threeMonthPlan = this.subscriptionConfig.pricing_groups.find(
        group => group.minimum_term === '3 months'
      );
      if (threeMonthPlan) {
        longTermPrice.textContent = `${this.formatMoney(threeMonthPlan.reccuring_price_cents)} / month`;
      }
    }
  }

  showLoadingState(message = 'Checking availability...') {
    // Show and add loading state to pdp-rates
    const pdpRatesBlock = document.querySelector('.pdp-rates');
    const pdpRatesSummary = document.querySelector('.pdp-rates__summary');
    
    if (pdpRatesBlock) {
      pdpRatesBlock.classList.add('show');
    }
    
    if (pdpRatesSummary) {
      pdpRatesSummary.classList.add('loading');
    }
  }

  hideLoadingState() {
    // Remove loading state from pdp-rates summary
    const pdpRatesSummary = document.querySelector('.pdp-rates__summary');
    if (pdpRatesSummary) {
      pdpRatesSummary.classList.remove('loading');
    }
  }

  showError(message) {
    console.error('Rental form error:', message);
    // Error is shown via disabled add to cart button
    // The pdp-rates block stays visible but without updated values
  }

  disableAddToCart() {
    if (this.elements.addToCartBtn) {
      this.elements.addToCartBtn.setAttribute('disabled', '');
      this.elements.addToCartBtn.setAttribute('aria-disabled', 'true');
    }
  }

  enableAddToCart() {
    if (this.elements.addToCartBtn) {
      this.elements.addToCartBtn.removeAttribute('disabled');
      this.elements.addToCartBtn.removeAttribute('aria-disabled');
    }
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback
    return `$${(cents / 100).toFixed(2)}`;
  }
}

// Register custom element
if (!customElements.get('rental-product-form')) {
  customElements.define('rental-product-form', RentalProductForm);
}

